import { useAsync } from "@/shared/hooks/HookBase";
import { useCallback } from "react";
import type IAccountManagerService from "../contracts/IAccountManagerService";
import AccountManagerService from "../services/AccountManagerService";
import type PageRequest from "@/shared/models/PageRequest";
import type CreateUser from "../models/CreateUser";

const accountManagerService: IAccountManagerService =
  new AccountManagerService();

export function useAccountManager() {
  const { loading, error, execute } = useAsync();

  const getPage = useCallback(
    async (pageRequest: PageRequest) => {
      return await execute(() => accountManagerService.getPage(pageRequest));
    },
    [execute],
  );

  const getAccount = useCallback(
    async (userId: string) => {
      return await execute(() => accountManagerService.get(userId));
    },
    [execute],
  );

  const createAccount = useCallback(
    async (newUser: CreateUser) => {
      return await execute(() => accountManagerService.create(newUser), { successMessage: "Cuenta creada correctamente" });
    },
    [execute],
  );

  const updateAccount = useCallback(
    async (id: string, newData: CreateUser) => {
      return await execute(
        async () => await accountManagerService.update(id, newData),
        { successMessage: "Cuenta actualizada correctamente" },
      );
    },
    [execute],
  );

  const deleteAccount = useCallback(
    async (id: string) => {
      return await execute(() => accountManagerService.delete(id), { successMessage: "Cuenta eliminada correctamente" });
    },
    [execute],
  );

  const resendConfirmation = useCallback(
    async (id: string) => {
      return await execute(() => accountManagerService.resendConfirmation(id), {
        successMessage: "Correo de confirmación reenviado",
      });
    },
    [execute],
  );

  const sendPasswordReset = useCallback(
    async (id: string) => {
      return await execute(() => accountManagerService.sendPasswordReset(id), {
        successMessage: "Correo de cambio de contraseña enviado",
      });
    },
    [execute],
  );

  const getMe = useCallback(async () => {
    return await execute(() => accountManagerService.getMe());
  }, [execute]);

  return {
    loading,
    error,
    getPage,
    getAccount,
    createAccount,
    updateAccount,
    deleteAccount,
    resendConfirmation,
    sendPasswordReset,
    getMe,
  };
}
